import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscriber } from 'rxjs';

import { TrialInsightsState } from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { getPatientCostSummaryQuery, SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { Utils } from '@shared/utils/utils';

import {
  TICPCChartType,
  TrialInsightsClinicalPatientCostChartService,
} from './patient-cost-chart.service';
import { TrialInsightsClinicalPatientCostTableService } from './patient-cost-table.service';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import {
  TrialInsightsPatientCostKey,
  TrialInsightsTableOptions,
} from '../../models/trial-insights-table.model';

type Results = [State['data'], TrialInsightsPatientCostKey, SortOrder];
type RowData = TrialInsightsTableOptions['rowData']['data'];
type State = Partial<TrialInsightsState['patientCost']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightsPatientCostKey.SITE,
    defaultOrder: SortOrder.DESC,
  },
  {
    buttonKey: TrialInsightsPatientCostKey.GROUP,
    defaultOrder: SortOrder.DESC,
  },
];

@Injectable()
export class TrialInsightsClinicalPatientCostQueryService extends GenericTrialInsightsQuery<TICPCChartType> {
  chartService: TrialInsightsClinicalPatientCostChartService;
  tableService: TrialInsightsClinicalPatientCostTableService;
  trialInsightsQuery: TrialInsightsQuery;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const chartService = inject(TrialInsightsClinicalPatientCostChartService);
    const tableService = inject(TrialInsightsClinicalPatientCostTableService);

    super({
      chartService,
      slice: 'patientCost',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.chartService = chartService;
    this.tableService = tableService;
  }

  processPatientCostSummary$(): Observable<Results> {
    return combineLatest([
      this.data as Observable<getPatientCostSummaryQuery[]>,
      this.selectedKey as BehaviorSubject<TrialInsightsPatientCostKey>,
      this.sortOrder,
    ]).pipe(this.processResponseData());
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      const [data, selectedKey, sortOrder] = sourceValue;

      if (!data || !data.length) {
        this.resetState();

        return;
      }

      const parsedData = this.parseStoreData(data);
      const costData = this.parseStoreDetail(parsedData, selectedKey, sortOrder);

      if (!costData) {
        return;
      }

      // Costs
      const costs =
        selectedKey === TrialInsightsPatientCostKey.SITE
          ? costData.site_costs
          : costData.patient_group_costs;

      // Patients Enrolled
      const patientsEnrolled = costData.total_cost?.patients_enrolled || 0;

      // Expected Enrolled
      const expectedEnrolled = costData.expected_patients_enrolled;

      this.expectedEnrolled.next(`${expectedEnrolled}`);

      // Expected Enrolled Exceeded
      const expectedEnrolledExceeded = patientsEnrolled > expectedEnrolled;

      this.expectedEnrolledExceeded.next(expectedEnrolledExceeded);

      // Expected Enrolled for Chart Max
      const expectedEnrolledChartMax = expectedEnrolledExceeded
        ? patientsEnrolled
        : expectedEnrolled;

      // Total Amount
      const totalAmount = costData.total_cost?.average_patient_cost || 0;

      this.totalAmount.next(Utils.currencyFormatter(totalAmount));

      // Legend Options
      const legendOptions = this.chartService.createLegend(patientsEnrolled);

      this.legendOptions.next(legendOptions);

      // Chart Options
      const dataset = this.chartService.createDatasets(patientsEnrolled);
      const chartOptions = this.chartService.createChart(dataset, expectedEnrolledChartMax);

      this.chartOptions.next(chartOptions);

      const rowData: RowData = costs.map((cost) => {
        const siteData = JSON.parse(cost.source_entity_data);

        return {
          buttonKey: selectedKey,
          leftHeader: siteData.name || '',
          leftSubheader: siteData.primary_investigator || '',
          rightHeader: Utils.currencyFormatter(cost.average_patient_cost),
          rightSubheader: `${cost.patients_enrolled} Patients`,
        };
      });

      // Table Options
      const tableOptions = this.tableService.createTable(selectedKey, rowData);

      this.tableOptions.next(tableOptions);

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }
}
